import styled, { css } from 'styled-components';
import { forwardRef } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import { ROUTES } from '_constants/routes';

import * as Layout from '_components/atomic/layouts/form';
import Icon from '_components/atomic/Icon';
import Button from '_components/atomic/Button';
import BaseBannerSection from '_components/composed/sections/BaseBannerSection';
import FAQSection from '_components/composed/sections/FAQSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import ThreeStepSection from '_components/composed/sections/ThreeStepSection';
import WhySection from '_components/composed/sections/WhySection';
import RightPartWhyTrademarkMonitoringSection from '_components/composed/sections/RightPartWhyTrademarkMonitoringSection';
import React from 'react';
import PriceSection from '_components/composed/sections/PriceSection';
import { useSelector } from 'react-redux';
import { selectPriceMapByProcessingCode } from '_redux/variants/selector';
import { PROCESSING_OPTIONS_KEYS } from '_constants/processing';
import { PRODUCT_CODES } from '_constants/products';

const { publicRuntimeConfig } = getConfig();
const { colors, devices, spaces } = theme;

const StyledTrademarkMonitoringPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${devices.tablet} {
    display: block;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const TrademarkMonitoringPage = () => {
  const router = useRouter();

  const priceMapByStandardProcessing = useSelector(
    selectPriceMapByProcessingCode(PROCESSING_OPTIONS_KEYS.standard)
  );

  const productPrice = priceMapByStandardProcessing[PRODUCT_CODES.tmMonitoring];
  return (
    <StyledTrademarkMonitoringPage>
      <BaseBannerSection
        hideImageMobile
        imgSrc={`${publicRuntimeConfig.basePath}/images/trademark-monitoring.svg`}
        title="Trademark Monitoring"
        text={
          <>
            Protect your Trademark!
            <br />
            Maintain vigilance of how your mark is being used and stop
            infringement in its tracks.
          </>
        }
        buttons={[
          {
            text: 'Protect Your Trademark',
            onClick: () => router.push(ROUTES.trademarkMonitoringFiling),
            color: theme.colors.blue[500],
            suffix: (
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.white} />
            ),
          },
        ]}
      />
      <ThreeStepSection
        title="3 Step Process"
        steps={[
          {
            header: 'Complete Our Online Questionnaire',
            description:
              'All information gathered is confidential and secured.',
            icon: `${publicRuntimeConfig.basePath}/images/my-answers-bubble.svg`,
          },
          {
            header: 'We Monitor Your Mark',
            description:
              'Our specialists compile a custom monitoring search strategy to watch for unwanted use of your brand.',
            icon: `${publicRuntimeConfig.basePath}/images/app-development-bubble.svg`,
          },
          {
            header: 'Report Delivery',
            description:
              'Receive your monthly report to review for potential infringement.',
            icon: `${publicRuntimeConfig.basePath}/images/app-filed-documents.svg`,
          },
        ]}
      />
      <Layout.Divider desktop size={spaces.large4X} />
      <WhySection
        leftImage={`${publicRuntimeConfig.basePath}/images/trademark-monitoring-why.png`}
        rightPart={<RightPartWhyTrademarkMonitoringSection />}
      />
      <PriceSection
        title="Trademark monitoring"
        subtitle="PROTECT AGAINST TRADEMARK INFRINGEMENT WITH OUR CUSTOMIZED MONITORING SYSTEM"
        price={productPrice}
        priceText="quarterly"
        bullets={[
          'Professional customer focused support from specialists',
          'Easy to read electronic report to stay informed on changes within the market',
        ]}
        buttons={[
          {
            text: 'Protect Your Trademark',
            color: colors.white,
            textColor: colors.blue[500],
            suffix: (
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.blue[500]} />
            ),
            onClick: () => router.push(ROUTES.trademarkMonitoringFiling),
          },
        ]}
      />
      <FAQSection />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={theme.colors.blue[500]}
              />
            }
            color={theme.colors.white}
            onClick={() => router.push(ROUTES.trademarkRegistrationFiling)}
            textColor={theme.colors.blue[500]}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.blue[500]}
          >
            Protect Your Trademark
          </Button>
        }
        imgSrc={`${publicRuntimeConfig.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle
        size={686}
        rotate={90}
        right={-135}
        top={'calc(30% + 10px)'}
      />
      <StyledCircle
        size={743}
        rotate={180}
        left={-197}
        top={'calc(35% + 20px)'}
      />
      <StyledImageContainer left={65} top={'calc(51% + 40px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle
        size={820}
        rotate={-180}
        right={-202}
        top={'calc(77% + 8px)'}
      />
      <StyledImageContainer right={65} top={'calc(83% + 20px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledTrademarkMonitoringPage>
  );
};

export default TrademarkMonitoringPage;
